import { useCallback, useEffect, useState } from 'react';
import topApi from '../../components/api/ToPApi';
import FormModal from '../../components/ui/FormModal';
import { Bank } from '../../models/interfaces';
import { errorMessage } from '../../utils/access';

export interface ApiClient {
    id: number;
    clientId: string;
    name: string;
    bankId: number;
    scopes: string[];
    status: 'ACT' | 'INA';
    lastUsedAt: string | null;
    createdAt: string | null;
}

// Credenciales recién generadas: el secreto solo se puede ver ahora
interface RevealedSecret {
    clientId: string;
    name: string;
    clientSecret: string;
    rotated: boolean;
}

type PendingAction = { type: 'rotate' | 'disable'; client: ApiClient };

const API_URL = process.env.REACT_APP_API_URL ?? '/api';

const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString('es-CR', { dateStyle: 'medium', timeStyle: 'short' }) : 'Nunca';

const CopyButton = ({ value, label }: { value: string; label: string }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => setCopied(false));
    };
    return (
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copy} aria-label={label} title={label}>
            <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'}`} />
        </button>
    );
};

// Alcances que se pueden dar a un sistema
const SCOPES = [
    { code: 'merchant-onboarding', label: 'Onboarding', title: 'Crear comercios' },
    { code: 'merchant-query', label: 'Consulta', title: 'Consultar comercios y sus funcionalidades' },
];

// Clientes de integración de un banco: sistemas externos que hacen onboarding y consultan comercios con JWT
const ApiClientsModal = ({ bank, onClose }: { bank: Bank; onClose: () => void }) => {
    const [clients, setClients] = useState<ApiClient[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [name, setName] = useState('');
    const [nameMissing, setNameMissing] = useState(false);
    const [newScopes, setNewScopes] = useState<string[]>(['merchant-onboarding']);
    const [working, setWorking] = useState(false);
    const [error, setError] = useState('');
    const [revealed, setRevealed] = useState<RevealedSecret | null>(null);
    const [pending, setPending] = useState<PendingAction | null>(null);

    const base = `/bank/${bank.id}/api-clients`;

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const resp = await topApi.get<{ clients: ApiClient[] }>(base);
            setClients(resp.data.clients ?? []);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setIsLoading(false);
        }
    }, [base]);

    useEffect(() => { load(); }, [load]);

    const replace = (client: ApiClient) => setClients(prev => prev.map(c => (c.id === client.id ? client : c)));

    const create = async () => {
        if (!name.trim()) { setNameMissing(true); return; }
        setWorking(true);
        setError('');
        try {
            const resp = await topApi.post<{ client: ApiClient; clientSecret: string }>(base, { name: name.trim(), scopes: newScopes });
            setClients(prev => [...prev, resp.data.client]);
            setRevealed({ clientId: resp.data.client.clientId, name: resp.data.client.name, clientSecret: resp.data.clientSecret, rotated: false });
            setName('');
            setNameMissing(false);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setWorking(false);
        }
    };

    const confirmPending = async () => {
        if (!pending) return;
        const { type, client } = pending;
        setPending(null);
        setWorking(true);
        setError('');
        try {
            if (type === 'rotate') {
                const resp = await topApi.post<{ client: ApiClient; clientSecret: string }>(`${base}/${client.id}/rotate`);
                replace(resp.data.client);
                setRevealed({ clientId: resp.data.client.clientId, name: resp.data.client.name, clientSecret: resp.data.clientSecret, rotated: true });
            } else {
                const resp = await topApi.put<{ client: ApiClient }>(`${base}/${client.id}/status`, { status: 'INA' });
                replace(resp.data.client);
            }
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setWorking(false);
        }
    };

    // Agrega o quita un alcance (siempre debe quedar al menos uno)
    const toggleScope = async (client: ApiClient, scope: string) => {
        const scopes = client.scopes.includes(scope) ? client.scopes.filter(s => s !== scope) : [...client.scopes, scope];
        if (!scopes.length) return;
        setWorking(true);
        setError('');
        try {
            const resp = await topApi.put<{ client: ApiClient }>(`${base}/${client.id}/scopes`, { scopes });
            replace(resp.data.client);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setWorking(false);
        }
    };

    const enable = async (client: ApiClient) => {
        setWorking(true);
        setError('');
        try {
            const resp = await topApi.put<{ client: ApiClient }>(`${base}/${client.id}/status`, { status: 'ACT' });
            replace(resp.data.client);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setWorking(false);
        }
    };

    return (
        <FormModal title="Integraciones" subtitle={bank.name} onClose={onClose} size="lg" error={error}>
            <p className="text-muted small">
                Credenciales para que los sistemas del banco hagan el onboarding de comercios por API y consulten sus comercios y
                funcionalidades. Cada sistema pide un token con su <code>client_id</code> y su secreto, y solo trabaja con comercios de este banco.
            </p>

            {revealed && (
                <div className="alert alert-warning" role="alert">
                    <div className="fw-semibold mb-2">
                        <i className="bi bi-shield-lock me-1" />
                        {revealed.rotated ? `Nuevo secreto de "${revealed.name}"` : `Credenciales de "${revealed.name}"`}
                    </div>
                    <p className="small mb-2">
                        Copie el secreto y entréguelo por un medio seguro. <strong>No se volverá a mostrar.</strong>
                        {revealed.rotated && ' El secreto anterior ya no sirve.'}
                    </p>
                    <label className="form-label small mb-1" htmlFor="revealedClientId">client_id</label>
                    <div className="input-group input-group-sm mb-2">
                        <input id="revealedClientId" className="form-control font-monospace" readOnly value={revealed.clientId} />
                        <CopyButton value={revealed.clientId} label="Copiar client_id" />
                    </div>
                    <label className="form-label small mb-1" htmlFor="revealedSecret">client_secret</label>
                    <div className="input-group input-group-sm mb-2">
                        <input id="revealedSecret" className="form-control font-monospace" readOnly value={revealed.clientSecret} />
                        <CopyButton value={revealed.clientSecret} label="Copiar secreto" />
                    </div>
                    <div className="text-end">
                        <button type="button" className="btn btn-sm btn-warning" onClick={() => setRevealed(null)}>Ya lo guardé</button>
                    </div>
                </div>
            )}

            {bank.status !== 'INA' && (
                <div className="mb-3">
                    <label htmlFor="apiClientName" className="form-label">Nuevo sistema</label>
                    <div className="input-group">
                        <input id="apiClientName" className={`form-control${nameMissing && !name.trim() ? ' is-invalid' : ''}`}
                            maxLength={100} placeholder="Ej.: Core del banco, sistema de afiliación"
                            value={name} onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); create(); } }} />
                        <button type="button" className="btn btn-primary" onClick={create} disabled={working}>
                            <i className="bi bi-key me-1" /> Crear credenciales
                        </button>
                        <div className="invalid-feedback">Indique el nombre del sistema</div>
                    </div>
                    <div className="d-flex flex-wrap gap-3 mt-2 small">
                        {SCOPES.map(scope => (
                            <div key={scope.code} className="form-check mb-0">
                                <input id={`newScope-${scope.code}`} type="checkbox" className="form-check-input"
                                    checked={newScopes.includes(scope.code)}
                                    disabled={newScopes.length === 1 && newScopes.includes(scope.code)}
                                    onChange={() => setNewScopes(prev => prev.includes(scope.code) ? prev.filter(s => s !== scope.code) : [...prev, scope.code])} />
                                <label htmlFor={`newScope-${scope.code}`} className="form-check-label">{scope.title}</label>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {pending && (
                <div className="alert alert-secondary d-flex flex-wrap align-items-center gap-2" role="alert">
                    <div className="flex-grow-1 small">
                        {pending.type === 'rotate'
                            ? <>¿Generar un secreto nuevo para <strong>{pending.client.name}</strong>? El actual dejará de servir y el sistema deberá actualizarlo.</>
                            : <>¿Desactivar <strong>{pending.client.name}</strong>? No podrá pedir tokens y los que ya tiene dejarán de funcionar.</>}
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPending(null)}>Cancelar</button>
                    <button type="button" className={`btn btn-sm ${pending.type === 'rotate' ? 'btn-primary' : 'btn-danger'}`} onClick={confirmPending}>
                        {pending.type === 'rotate' ? 'Generar secreto' : 'Desactivar'}
                    </button>
                </div>
            )}

            <div className="table-responsive">
                <table className="table table-sm align-middle mb-2">
                    <thead>
                        <tr className="text-muted small text-uppercase">
                            <th>Sistema</th>
                            <th>client_id</th>
                            <th>Alcances</th>
                            <th>Estado</th>
                            <th>Último uso</th>
                            <th style={{ textAlign: 'center' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={6} className="text-center py-3"><span className="spinner-border spinner-border-sm me-2" /> Cargando...</td></tr>
                        ) : clients.length > 0 ? clients.map(client => (
                            <tr key={client.id}>
                                <td className="fw-medium">{client.name}</td>
                                <td>
                                    <span className="font-monospace small me-1">{client.clientId}</span>
                                    <CopyButton value={client.clientId} label={`Copiar client_id de ${client.name}`} />
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    {SCOPES.map(scope => {
                                        const on = client.scopes.includes(scope.code);
                                        return (
                                            <button key={scope.code} type="button" aria-pressed={on} disabled={working || (on && client.scopes.length === 1)}
                                                className={`btn btn-sm rounded-pill me-1 py-0 ${on ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                title={`${scope.title}: ${on ? 'quitar' : 'dar'} permiso`}
                                                onClick={() => toggleScope(client, scope.code)}>
                                                {scope.label}
                                            </button>
                                        );
                                    })}
                                </td>
                                <td>
                                    {client.status === 'INA'
                                        ? <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">Inactivo</span>
                                        : <span className="badge rounded-pill bg-success-subtle text-success-emphasis">Activo</span>}
                                </td>
                                <td className="small text-muted">{formatDate(client.lastUsedAt)}</td>
                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                    {client.status === 'ACT' ? (
                                        <>
                                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={working}
                                                title="Generar secreto nuevo" aria-label={`Generar secreto nuevo de ${client.name}`}
                                                onClick={() => setPending({ type: 'rotate', client })}>
                                                <i className="bi bi-arrow-repeat" />
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-danger ms-1" disabled={working}
                                                title="Desactivar" aria-label={`Desactivar ${client.name}`}
                                                onClick={() => setPending({ type: 'disable', client })}>
                                                <i className="bi bi-slash-circle" />
                                            </button>
                                        </>
                                    ) : (
                                        <button type="button" className="btn btn-sm btn-outline-success" disabled={working}
                                            title="Activar" aria-label={`Activar ${client.name}`} onClick={() => enable(client)}>
                                            <i className="bi bi-check-circle" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan={6} className="text-center py-3 text-muted">Este banco no tiene sistemas integrados.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <details className="small text-muted">
                <summary>Cómo se usa</summary>
                <ol className="mt-2 mb-0 ps-3">
                    <li>Token: <code>POST {API_URL}/external/auth/token</code> con <code>client_id</code> y <code>client_secret</code> (dura 30 minutos).</li>
                    <li>Onboarding: <code>POST {API_URL}/external/merchants/onboarding</code> con <code>Authorization: Bearer &lt;token&gt;</code>.</li>
                    <li>Consulta: <code>GET {API_URL}/external/merchants</code> y <code>GET {API_URL}/external/merchants/&lt;id&gt;</code> (comercios y funcionalidades activas), <code>GET {API_URL}/external/features</code> (catálogo y precios).</li>
                </ol>
            </details>
        </FormModal>
    );
};

export default ApiClientsModal;
